<template>
  <div class="grid grid-rows-[auto_1fr] overflow-hidden h-full">
    <ThreadsNav :show-back="true" :show-center="false">
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>

    <div v-if="loading">
      <div class="flex justify-center items-center pt-32">
        <i class="loading loading-ring loading-lg" />
      </div>
    </div>
    <!-- 主要内容区域 -->

    <div v-else class="flex flex-col h-full overflow-y-auto">
      <!-- 图片展示区域 -->
      <div
        v-if="mateWish.resultImagezz?.length"
        class="bg-gradient-to-b from-base-200 to-base-100"
      >
        <!-- 主图 -->
        <div class="relative w-full bg-base-300">
          <img
            :src="mateWish.resultImagezz[activeImageIndex]?.url"
            alt="Wish Detail"
            class="w-full h-full object-contain cursor-pointer"
            crossorigin="anonymous"
            @click="
              useGlobalModal().openMediaPreviewModal(
                mateWish.resultImagezz.map((v) => v.url),
                activeImageIndex
              )
            "
          />

          <!-- 图片指示器 -->
          <div
            v-if="mateWish.resultImagezz.length > 1"
            class="absolute top-4 right-4 bg-black/60 text-white px-2 py-1 rounded-full text-sm"
          >
            {{ activeImageIndex + 1 }} / {{ mateWish.resultImagezz.length }}
          </div>
        </div>

        <!-- 缩略图条 -->
        <div v-if="mateWish.resultImagezz.length > 1" class="p-3">
          <div class="flex gap-2 overflow-x-auto hide-scrollbar">
            <div
              v-for="(item, i) in mateWish.resultImagezz"
              :key="item.id + i"
              class="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden cursor-pointer transition-all duration-200"
              :class="{
                'ring-2 ring-primary ring-offset-2': i === activeImageIndex,
                'opacity-60': i !== activeImageIndex,
              }"
              @click="activeImageIndex = i"
            >
              <img
                :src="item.url"
                alt=""
                class="w-full h-full object-cover"
                crossorigin="anonymous"
              />
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="p-4">
          <div class="flex justify-center gap-3 flex-wrap">
            <button
              class="btn btn-xs"
              @click="clickToMoment(mateWish.resultImagezz[activeImageIndex])"
            >
              {{ $t(`mateWish.toMoment`) }}
            </button>
            <!-- <button
              class="btn btn-primary btn-xs"
              @click="
                clickToGallery(mateWish.resultImagezz[activeImageIndex].id)
              "
            >
              {{ $t(`mateWish.saveToGallery`) }}
            </button> -->

            <button class="btn btn-xs" @click="clickToEdit">继续修改</button>
            <button class="btn btn-xs" @click="clickToRetry">
              再来一张
              <PowerIcon />
              10
            </button>
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="p-4 space-y-4">
        <!-- 描述文本区域 -->
        <div class="card bg-base-200 shadow-sm">
          <div class="card-body p-4">
            <h3 class="card-title text-lg mb-2">祈愿描述</h3>
            <p class="text-base-content/80 leading-relaxed whitespace-pre-line">
              {{ mateWish.content }}
            </p>
          </div>
        </div>

        <!-- 祈愿主角 -->
        <div
          v-if="mateWish.characterzz?.length"
          class="card bg-base-200 shadow-sm"
        >
          <div class="card-body p-4">
            <h3 class="card-title text-lg mb-3">{{ $t(`mateWish.lead`) }}</h3>
            <div class="flex gap-3 overflow-x-auto hide-scrollbar">
              <div
                v-for="c in mateWish.characterzz"
                :key="c.mediaUrl"
                class="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden bg-base-300"
              >
                <BaseImg
                  :url="c.mediaUrl"
                  ratio="3:4"
                  fit="cover"
                  class="w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 祈愿参考图 -->
        <div
          v-if="mateWish.referenceImagezz?.length"
          class="card bg-base-200 shadow-sm"
        >
          <div class="card-body p-4">
            <h3 class="card-title text-lg mb-3">
              {{ $t(`mateWish.reference`) }}
            </h3>
            <div class="flex gap-3 overflow-x-auto hide-scrollbar">
              <div
                v-for="(img, idx) in mateWish.referenceImagezz"
                :key="img.url"
                class="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden bg-base-300"
                @click="
                  modalStore.openMediaPreviewModal(
                    mateWish.referenceImagezz.map((v) => v.url),
                    idx
                  )
                "
              >
                <BaseImg
                  :url="img.url"
                  ratio="3:4"
                  fit="cover"
                  class="w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createOneMateWish, readOneMateWish } from "@/apis/mate";
import api from "@/modules/api";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { GALLERY_MODULE_ID } from "@/stores/module/moduleMate";
import { useUI } from "@/stores/ui";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { Mate, MateWish, Media } from "@/types/entity";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

const loading = ref(true);
const route = useRoute();
const router = useRouter();
const mateWish = ref<MateWish>({} as MateWish);
const { t } = useI18n();
const activeImageIndex = ref(0);

onMounted(async () => {
  const uuid = route.params.uuid as string;
  loading.value = true;
  const res = await readOneMateWish(uuid);
  mateWish.value = res.item;
  loading.value = false;
});

const clickToMoment = (media: Media) => {
  sessionStorageSet("import-moment", [
    {
      message: mateWish.value!.content,
      imgList: [{ url: media.url, mediaId: media.id }],
      videoList: [],
      sharedMessageList: [],
      visibility: 1,
      mentionMateIdzz: [],
      isAiGenerated: false,
    },
  ]);
  router.push({ name: `createThread` });
};

const uiStore = useUI();
const clickToGallery = (mediaId: number) => {
  uiStore.popMateSelector({
    mateOnly: true,
    callback: async (mate: Mate) => {
      console.log(mate);
      const loading = showLoadingToast(t(`Message.saving`));
      try {
        await api.createOneMateMedia({
          mediaId,
          type: 1,
        });
        loading.close();
        try {
          await showConfirmDialog({
            message: t(`mateWish.saveSuccessConfirmToGallery`),
            confirmButtonText: t(`Buttons.confirm`),
            cancelButtonText: t(`Buttons.cancel`),
          });
          router.push({
            name: "mateEdit",
            params: {
              uuid: mate.uuid,
            },
            hash: `#${GALLERY_MODULE_ID}`,
          });
        } catch {
          return;
        }
      } catch (e) {
        loading.close();
      }
    },
  });
};

const modalStore = useGlobalModal();
const userStore = useGlobalUser();
const clickToRetry = async () => {
  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: 10,
      cb: clickToRetry,
    })
  )
    return;

  const loading = showLoadingToast(t(`Message.loading`));
  try {
    await createOneMateWish({
      // characterIdzz: mateWish.value.characterzz?.map((v) => v.id),
      referenceImageIdzz: mateWish.value.referenceImagezz?.map((v) => v.id),
      content: mateWish.value.content,
      ratio: mateWish.value.ratio,
      generationType: 1,
    });

    userStore.refresh();
    loading.close();
    showSuccessToast({
      message: t(`Message.opSuccess`),
      forbidClick: true,
      onClose: () => {
        router.replace({ name: "mateWishHome" });
      },
    });

    sessionStorageSet("mate-wish-update", 1);
  } catch (e) {
    loading.close();
  }
};

const clickToEdit = () => {
  sessionStorageSet("mate-wish-edit", {
    content: "",
    referenceImagezz: mateWish.value.resultImagezz,
  });
  router.push({ name: "mateWishEdit" });
};
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
